import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Phase 12 — ProductRailGroupComponent.
 *
 * Collapsible group container for the /products rail. Copied from the
 * rates RailGroupComponent (generic, no rates coupling) into the
 * product-search workstream to keep /products self-contained per
 * CLAUDE.md workstream isolation. Distinct selector
 * (`app-product-rail-group`) avoids any ambiguity with the rates one.
 *
 *   · Header: caret + ALL-CAPS name + value-summary badge
 *   · Body: ng-content for the inline filter pickers
 *   · Closed until clicked (rates-rail rule); active filters show the blue
 *     `is-active` badge but do NOT auto-expand the group
 *   · forceOpen (find-a-filter) > forceClosed (single-expand) > internal
 *   · [hidden] beats :host{display:contents} via :host([hidden])
 */
@Component({
  selector: 'app-product-rail-group',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="rail-group" [class.is-open]="isOpen()">
      <button type="button"
              class="rail-group-head"
              (click)="toggle()"
              [attr.aria-expanded]="isOpen()">
        <!-- 2026-06-04 icing #3: text caret → rotating chevron SVG that
             matches the rates rail. The rates rail uses
             "M9 6l6 6-6 6" with 90° rotation on .is-open; copying that
             pattern here harmonizes the two rails. -->
        <svg class="caret" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"
             aria-hidden="true">
          <path d="M9 6l6 6-6 6"/>
        </svg>
        <span class="name">{{ name }}</span>
        @if (badge) {
          <span class="badge" [class.is-active]="activeCount > 0">{{ badge }}</span>
        }
      </button>
      @if (isOpen()) {
        <div class="rail-group-body">
          <ng-content />
        </div>
      }
    </div>
  `,
  styles: [`
    :host { display: contents; }
    :host([hidden]) { display: none; }

    /* A divider between groups, as /rates draws them (.tp-group border-top,
       none on the first). It used to be cancelled by
       .rail-group:first-of-type — but each .rail-group is the only child of
       its own host element, so EVERY group was "first of type" and the whole
       rail lost its separators. The first-group check now happens on the host,
       which is what sits next to its siblings. */
    .rail-group { border-top: 1px solid var(--border-light); }
    :host(:first-child) .rail-group { border-top: none; }

    /* 2026-06-04 icing #3: harmonize with rates' .tp-group-hdr —
       11px / 700 / uppercase / 0.08em / var(--text2). The two rails
       (Rates + Products) now share a single typographic system for
       collapsible group headers. */
    .rail-group-head {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 14px 7px 15px;
      background: transparent;
      border: none;
      cursor: pointer;
      font-family: inherit;
      text-align: left;
      color: var(--text2);
      transition: background 0.12s, color 0.12s;

      &:hover { background: var(--blue-soft); color: var(--navy); }
    }

    .rail-group.is-open .rail-group-head { background: var(--blue-soft); }

    .caret {
      width: 11px;
      height: 11px;
      color: var(--muted);
      transition: transform 0.18s ease, color 0.12s;
      flex-shrink: 0;
      margin-right: -2px;
    }
    .rail-group.is-open .caret { transform: rotate(90deg); color: var(--navy); }

    .name {
      font-size: 11px;
      font-weight: 700;
      color: inherit;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }
    .rail-group.is-open .name { color: var(--navy); }

    .badge {
      margin-left: auto;
      font-size: 11.5px;
      font-weight: 500;
      color: var(--muted);
      font-family: inherit;
      text-transform: none;
      letter-spacing: 0;
      max-width: 150px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &.is-active { color: var(--blue); font-weight: 600; }
    }

    /* Vertical padding matches /rates' .tp-group-body (2px / 6px); it was
       6px / 12px, which with the pickers' own bottom padding left a wide gap
       under the last option (e.g. Fee-Based) before the next group. The 14px
       sides are unchanged — .states-body's negative margins are computed
       from them. */
    /* A hairline between the filter rows inside one group (Carriers / Premium
       Type / A.M. Best / Share Class), as /rates separates its rows
       ([data-dim] + [data-dim]). ::ng-deep because the rows are projected
       content, styled by the parent's encapsulation, not this component's. */
    .rail-group-body ::ng-deep > * + * {
      border-top: 1px solid var(--border-light);
      margin-top: 2px;
      padding-top: 2px;
    }

    .rail-group-body {
      padding: 2px 14px 6px;
      animation: slide-in 0.14s ease-out;
    }

    @keyframes slide-in {
      from { opacity: 0; transform: translateY(-4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
  `],
})
export class ProductRailGroupComponent {
  @Input({ required: true }) name!: string;
  @Input() badge: string = 'All';
  @Input() activeCount: number = 0;
  @Input() forceOpen: boolean = false;
  @Input() forceClosed: boolean = false;
  @Output() expandRequest = new EventEmitter<void>();
  @Output() collapseEvent = new EventEmitter<void>();

  private readonly _userToggled = signal(false);
  private readonly _expanded = signal(false);

  isOpen(): boolean {
    if (this.forceOpen) return true;
    if (this.forceClosed) return false;
    // Closed until clicked (matches the rates rail rule). An active filter no
    // longer auto-expands the group — the blue `is-active` badge indicates it.
    // find-a-filter (forceOpen) still reveals matching groups.
    return this._userToggled() ? this._expanded() : false;
  }

  toggle(): void {
    this._userToggled.set(true);
    const next = !this.isOpen();
    this._expanded.set(next);
    if (next) this.expandRequest.emit();
    else this.collapseEvent.emit();
  }
}
